import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "card", "modal", "modalImage", "modalPlaceholder"] as const
  static values = { selected: Number }

  declare readonly inputTarget: HTMLInputElement
  declare readonly cardTargets: HTMLElement[]
  declare readonly modalTarget: HTMLElement
  declare readonly modalImageTarget: HTMLImageElement
  declare readonly modalPlaceholderTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly hasModalImageTarget: boolean
  declare readonly hasModalPlaceholderTarget: boolean
  declare readonly hasSelectedValue: boolean
  declare selectedValue: number

  private readonly handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  connect() {
    const currentValue = this.inputTarget.value

    if (currentValue) {
      this.selectedValue = Number(currentValue)
    } else if (this.hasSelectedValue && this.selectedValue) {
      this.inputTarget.value = String(this.selectedValue)
    }

    this.highlightSelection()
  }

  selectCard(event: Event) {
    event.stopPropagation()
    const card = this.cardFromEvent(event)
    if (!card) return

    const styleId = card.dataset.bookStyleSelectionStyleId
    if (!styleId) return

    this.selectedValue = Number(styleId)
    this.inputTarget.value = styleId
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.highlightSelection()
  }

  openModal(event: Event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasModalTarget) return

    const card = this.cardFromEvent(event)
    if (!card) return

    const previewUrl = this.previewUrlForCard(card)
    this.toggleModalContent(previewUrl)

    this.modalTarget.classList.remove("hidden")
    this.modalTarget.focus()
    document.addEventListener("keydown", this.handleEscape)
  }

  closeModal(event?: Event) {
    if (!this.hasModalTarget) return

    event?.preventDefault()
    event?.stopPropagation()

    this.modalTarget.classList.add("hidden")
    this.modalTarget.setAttribute("aria-hidden", "true")

    if (this.hasModalImageTarget) {
      this.modalImageTarget.src = ""
    }

    document.removeEventListener("keydown", this.handleEscape)
  }

  private highlightSelection() {
    this.cardTargets.forEach((card) => {
      const isSelected = Number(card.dataset.bookStyleSelectionStyleId) === this.selectedValue
      card.dataset.selected = String(isSelected)

      card.classList.toggle("border-primarygreen", isSelected)
      card.classList.toggle("border-gray-200", !isSelected)
      card.classList.toggle("shadow-lg", isSelected)
      card.classList.toggle("outline-primarygreen/30", isSelected)
      card.classList.toggle("outline", isSelected)
      card.classList.toggle("outline-transparent", !isSelected)

      const badge = card.querySelector<HTMLElement>("[data-role='status-badge']")
      if (badge) {
        const defaultLabel = badge.dataset.defaultLabel || "Select"
        const selectedLabel = badge.dataset.selectedLabel || "Selected"
        badge.textContent = isSelected ? selectedLabel : defaultLabel
        badge.classList.toggle("bg-gray-100", !isSelected)
        badge.classList.toggle("bg-[#D4E5E7]", isSelected)
        badge.classList.toggle("text-gray-600", !isSelected)
        badge.classList.toggle("text-primarygreen", isSelected)
      }

      const selectButton = card.querySelector<HTMLElement>("[data-role='select-button']")
      if (selectButton) {
        const defaultLabel = selectButton.dataset.defaultLabel || "Select"
        const selectedLabel = selectButton.dataset.selectedLabel || "Selected"
        selectButton.textContent = isSelected ? selectedLabel : defaultLabel
        selectButton.classList.toggle("bg-primarygreen", isSelected)
        selectButton.classList.toggle("text-white", isSelected)
        selectButton.classList.toggle("text-primarygreen", !isSelected)
        selectButton.classList.toggle("border-primarygreen", true)
      }
    })
  }

  private cardFromEvent(event: Event) {
    const target = event.currentTarget as HTMLElement | null
    return target?.closest<HTMLElement>("[data-book-style-selection-style-id]") || null
  }

  private previewUrlForCard(card: HTMLElement) {
    const language = this.currentLanguage()
    if (language === "de") {
      return card.dataset.previewDe || card.dataset.previewEn || ""
    }

    return card.dataset.previewEn || card.dataset.previewDe || ""
  }

  private currentLanguage() {
    const checked = document.querySelector<HTMLInputElement>('input[name="book[language]"]:checked')
    return checked?.value || "en"
  }

  private toggleModalContent(url: string) {
    if (!this.hasModalImageTarget || !this.hasModalPlaceholderTarget) return

    if (url) {
      this.modalImageTarget.src = url
      this.modalImageTarget.classList.remove("hidden")
      this.modalPlaceholderTarget.classList.add("hidden")
    } else {
      this.modalImageTarget.src = ""
      this.modalImageTarget.classList.add("hidden")
      this.modalPlaceholderTarget.classList.remove("hidden")
    }

    this.modalTarget.setAttribute("aria-hidden", "false")
  }
}
